import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, Package, Truck, Warning, ClockCounterClockwise } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TrazabilidadPublicaPage() {
  const { lid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/trazabilidad/${lid}`);
        setData(data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "Lote no encontrado" : "Error al consultar trazabilidad");
      }
    })();
  }, [lid]);

  const fmt = (s) => { if (!s) return "—"; const [y, m, d] = s.split("-"); return `${d}/${m}/${y}`; };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-900 to-[#921A0E]/50 flex items-center justify-center p-4 lg:p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
          <Warning size={60} weight="duotone" className="mx-auto text-rose-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">{error}</h1>
          <p className="text-sm text-neutral-500">Verificá que el QR sea válido o contactá al frigorífico.</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Cargando…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-200 p-4 sm:p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-[#921A0E] text-white px-6 py-5">
            <div className="text-xs uppercase font-mono opacity-80 tracking-wider">La Biferia · Trazabilidad</div>
            <div className="text-3xl font-bold font-heading mt-1">Lote {data.numero}</div>
            <div className="text-sm opacity-90 mt-1">{data.descripcion}</div>
          </div>
          <div className="p-4 space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <InfoCard label="Fecha de Elaboración" value={fmt(data.fecha_elaboracion)} icon={Package} color="text-[#921A0E]" />
              <InfoCard label="Fecha de Vencimiento" value={fmt(data.fecha_vencimiento)} icon={ClockCounterClockwise} color="text-amber-700" />
              <InfoCard label="Kilos ingresados" value={`${data.kilos_entrada || 0} kg`} icon={Package} color="text-neutral-700" />
              <InfoCard label="Estado del lote" value={data.estado?.toUpperCase() || "—"} icon={CheckCircle}
                color={data.estado === "activo" ? "text-emerald-700" : "text-rose-700"} />
            </div>

            {data.origenes?.length > 0 && (
              <div>
                <div className="text-xs uppercase font-mono text-neutral-500 mb-2 flex items-center gap-1">
                  <Truck size={14} /> Origen
                </div>
                <div className="space-y-2">
                  {data.origenes.map((o, i) => (
                    <div key={i} className="border border-neutral-200 rounded-lg p-3 bg-neutral-50">
                      <div className="font-medium">{o.proveedor || "—"}</div>
                      <div className="text-xs text-neutral-500 mt-1">
                        {o.compra_numero && `Compra ${o.compra_numero} · `}{o.peso_real} kg
                        {o.condicion !== "ok" && <span className="ml-1 uppercase font-mono text-amber-700"> ({o.condicion})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.consumos?.length > 0 && (
              <div>
                <div className="text-xs uppercase font-mono text-neutral-500 mb-2">Consumos / Salidas</div>
                <div className="space-y-1">
                  {data.consumos.map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-neutral-100 py-2">
                      <div className="text-neutral-600">
                        <span className="uppercase font-mono text-xs bg-neutral-200 px-1.5 py-0.5 rounded">{c.tipo}</span>
                        <span className="ml-2">{c.descripcion || "—"}</span>
                      </div>
                      <div className="font-mono text-neutral-800">{c.kilos} kg</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="bg-neutral-50 px-6 py-3 text-center text-xs text-neutral-500 border-t border-neutral-200">
            La Biferia S.R.L. · Frigorífico bajo control sanitario
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoCard({ label, value, icon: Icon, color = "text-neutral-800" }) {
  return (
    <div className="border border-neutral-200 rounded-lg p-3 bg-neutral-50">
      <div className="flex items-center gap-1 text-xs uppercase font-mono text-neutral-500 mb-1">
        <Icon size={12} /> {label}
      </div>
      <div className={`text-lg font-semibold ${color}`}>{value}</div>
    </div>
  );
}
